<div class="reset-page">
    <div class="reset-card">
        <button type="button" class="back-link" (click)="continueToSignIn()">
            ← Back to sign in
        </button>

        <div class="stepper">
            <span class="step-line" [class.active]="step >= 1"></span>
            <span class="step-line" [class.active]="step >= 2"></span>
            <span class="step-line" [class.active]="step >= 3"></span>
        </div>

        <!-- Step 1 -->
        @if (step === 1) {
        <ng-container>
            <div class="icon-badge">
                <div class="icon-mask" [style.--icon-url]="'url(assets/icons/email.svg)'"></div>
            </div>
            <h1>Reset your password</h1>
            <p class="subtitle">
                Enter the email or phone number linked to your account.
                We'll send you a verification code.
            </p>

            <form [formGroup]="emailForm" (ngSubmit)="onSubmitEmail()" class="form-block">

                <app-login-email-input formControlName="email" label="Email" placeholder="name@company.com" inputId="email">
                </app-login-email-input>

                @if (errorMessage()) {
                <p class="error">{{ errorMessage() }}</p>
                }

                <button class="primary-btn" type="submit" [disabled]="isSubmitting">
                    {{ isSubmitting ? 'Sending...' : 'Send verification code →' }}
                </button>
            </form>
        </ng-container>
        }

        <!-- Step 2 -->
        @if(step === 2) {
        <ng-container>
            <div class="icon-badge">
                <div class="icon-mask" style="height: 34px;" [style.--icon-url]="'url(assets/icons/lock.svg)'"></div>
            </div>
            <h1>Enter verification code</h1>
            <p class="subtitle">We've sent a verification code to your email. If you don't see it, check your spam
                folder.</p>

            <form [formGroup]="otpForm" (ngSubmit)="onVerifyOtp()" class="form-block">
                <div class="otp-group" (paste)="onOtpPaste($event)">
                    @for (control of otpArray.controls; track $index; let i = $index) {
                    <input #otpInput class="otp-box" type="text" inputmode="numeric" maxlength="1"
                        autocomplete="one-time-code" [formControl]="control" (input)="onOtpInput(i, $event)"
                        (keydown)="onOtpKeyDown(i, $event)" />
                    }
                </div>

                @if(errorMessage()) {
                <p class="error">{{ errorMessage() }}</p>
                }

                <div class="countdown-row">
                    <div style="display: flex; align-items: center; gap: 10px;">
                        <div class="icon-mask-clock" [style.--icon-url]="'url(assets/icons/clock.svg)'"></div>

                        Code expires in
                    </div>
                    <strong>{{ formattedCountdown }}</strong>
                </div>

                <button class="primary-btn muted" type="submit" [disabled]="!isOtpComplete || isSubmitting">
                    {{ isSubmitting ? 'Verifying...' : 'Verify' }}
                </button>

                <button type="button" class="text-btn" [disabled]="countdown > 540 || resendLoading"
                    (click)="resendCode()">
                    {{ resendLoading ? 'Resending...' : 'Resend code' }}
                </button>
            </form>
        </ng-container>
        }



        <!-- Step 3 -->
        @if(step === 3) {
        <ng-container>
            <div class="icon-badge">
                <div class="icon-mask" [style.--icon-url]="'url(assets/icons/email.svg)'"></div>
            </div>
            <h1>Create new password</h1>
            <p class="subtitle">Choose a strong password you haven't used before.</p>

            <form [formGroup]="passwordForm" (ngSubmit)="onResetPassword()" class="form-block">
                <div class="field-group">
                    <label class="field-label" for="password">Password</label>
                    <div class="input-wrapper" [class.input-wrapper--error]="passwordControl.invalid &&  submitted()">
                        <span class="input-icon">
                            <div class="password-icon-mask"
                                style="background-color: var(--text-muted); width: 14px; height: 16px;"
                                [style.--icon-url]="'url(assets/icons/password.svg)'"></div>
                        </span>
                        <input id="password" class="input-field" [type]="showPassword() ? 'text' : 'password'"
                            placeholder="Enter password" autocomplete="current-password" formControlName="password"
                            [formControl]="$any(passwordControl)" />
                        <button type="button" class="toggle-password" (click)="togglePassword()"
                            [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'">
                            @if (showPassword()) {
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                <path
                                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                            } @else {
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                            }
                        </button>
                    </div>
                    @if (passwordControl.invalid && passwordControl.touched) {
                    <span class="field-error">Please enter a valid password.</span>
                    }
                </div>

                <div class="strength-row" [ngClass]="{
                        weak: passwordStrength.label === 'WEAK',
                        medium: passwordStrength.label === 'MEDIUM',
                        strong: passwordStrength.label === 'STRONG'
                    }">
                    <div class="bars">
                        <span [class.filled]="passwordStrength.score >= 1"></span>
                        <span [class.filled]="passwordStrength.score >= 2"></span>
                        <span [class.filled]="passwordStrength.score >= 3"></span>
                        <span [class.filled]="passwordStrength.score >= 4"></span>
                    </div>
                    <strong class="strength-label">{{ passwordStrength.label }}</strong>
                </div>

                <ul class="rules">
                    <li [class.done]="passwordChecks.minLength">At least 8 characters</li>
                    <li [class.done]="passwordChecks.mixedCase">Uppercase + lowercase letters</li>
                    <li [class.done]="passwordChecks.number">One number</li>
                    <li [class.done]="passwordChecks.specialChar">
                        At least 1 special character (!@#$)
                    </li>
                </ul>

                <div class="field-group">
                    <label class="field-label" for="confirmPassword">Password</label>
                    <div class="input-wrapper" [class.input-wrapper--error]="passwordControl.invalid &&  submitted()">
                        <span class="input-icon">
                            <div class="password-icon-mask" [style.--icon-url]="'url(assets/icons/password.svg)'"></div>
                        </span>
                        <input id="confirmPassword" class="input-field"
                            [type]="showConfirmPassword() ? 'text' : 'password'" placeholder="Enter password"
                            autocomplete="current-password" formControlName="confirmPassword"
                            [formControl]="$any(confirmPasswordControl)" />
                        <button type="button" class="toggle-password" (click)="toggleConfirmPassword()"
                            [attr.aria-label]="showConfirmPassword() ? 'Hide password' : 'Show password'">
                            @if (showConfirmPassword()) {
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                <path
                                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                            } @else {
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                            }
                        </button>
                    </div>
                    @if (confirmPasswordControl.invalid && confirmPasswordControl.touched) {
                    <span class="field-error">Please enter a valid password.</span>
                    }

                    @if (passwordForm.hasError('passwordMismatch') && confirmPasswordControl.touched) {
                    <span class="field-error">Passwords do not match.</span>
                    }
                </div>

                <button class="primary-btn muted" type="submit" [disabled]="!canSubmitPassword || isSubmitting">
                    {{ isSubmitting ? 'Resetting...' : 'Reset password' }}
                </button>
            </form>
        </ng-container>
        }

        <!-- Step 4 -->
        @if(step === 4) {
        <ng-container>
            <div class="icon-badge success">✓</div>
            <h1>Password reset</h1>
            <p class="subtitle">
                Your password has been updated. You can now sign in with your new credentials.
            </p>

            <div class="form-block">
                <div class="help-box">
                    <strong>Need help?</strong>
                    <span>
                        Make sure you're using the address registered to your OpsFlow account, or contact
                        your administrator for recovery assistance.
                    </span>
                </div>

                <button class="primary-btn" type="button" (click)="continueToSignIn()">
                    Continue to sign in
                </button>
            </div>

        </ng-container>
        }
    </div>
</div>